import React, { useMemo } from 'react';
import mixpanel, { Config } from 'mixpanel-browser';
import { isDev } from 'common/DevOnly';
import { Provider, shouldIgnoreMixpanel } from './useMixpanel';
import { bpeConfig } from '@enterprise/common';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { sessionReplayConfig } from 'integrations/mixpanel/sessionReplayConfig';
import { mockMixpanel } from 'integrations/mixpanel/mockMixpanel';

// All tenants share a single EU data-residency Mixpanel project, so every
// request (ingestion and session replay) must go to the EU endpoint. US-to-EU
// data forwarding was sunset in July 2026, so anything sent to the default US
// endpoint would be permanently dropped.
const MIXPANEL_API_HOST = 'https://api-eu.mixpanel.com';

interface MixpanelProviderProps {
  config?: Partial<Config>;
  name?: string;
  token?: string;
}

const MixpanelProvider: React.FC<React.PropsWithChildren<MixpanelProviderProps>> = ({
  children,
  config,
  name,
  token
}) => {
  const mixpanelSessionRecording = useDarkly().mixpanelSessionRecording;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const mixpanelConfig: Partial<Config> = useMemo(
    () => ({
      // eslint-disable-next-line id-denylist
      debug: isDev,
      ignore_dnt: isDev,
      api_host: MIXPANEL_API_HOST,
      ...(!!mixpanelSessionRecording ? sessionReplayConfig(mixpanelSessionRecording) : {}),
      ...config
    }),
    [mixpanelSessionRecording, config]
  );

  const projectToken = bpeConfig.integrations.mixpanelAccessToken;
  const mixpanelLib = !shouldIgnoreMixpanel && (token || projectToken) ? mixpanel : mockMixpanel;

  mixpanelLib.init(token || projectToken || '', mixpanelConfig, name || '');

  return <Provider value={mixpanelLib}>{children}</Provider>;
};

export default MixpanelProvider;
